import * as Colors from '@brightlayer-ui/colors';
import { EmptyState } from '@brightlayer-ui/react-components';
import { AddCircle, Group } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { ErrorState } from '../../components/ErrorState';
import { NavBarButton } from '../../components/NavBarButton';
import { PageWrapper } from '../../components/PageWrapper';
import { Spinner } from '../../components/Spinner';
import { FetchedState, getUserRoles, UserSelectors } from '../../features/userSlice';
import { ListOrganizations } from './ListOrganizations';

const styles = {
    listBox: {
        marginTop: '25px',
    },
    emptyState: {
        alignContent: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    emptyStateIcon: {
        alignItems: 'center',
        fontSize: '140px',
    },
    contentBody: { marginTop: '20px' },
    contentBox: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'row',
        paddingLeft: 4,
    },
};

export const OrganizationListPage: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const organizations = useAppSelector(UserSelectors.userOrganizationRoles);
    const fetchedState = useAppSelector(UserSelectors.userOrganizationRolesFetchedState);

    const [showLoader, setShowLoader] = useState(fetchedState !== FetchedState.FETCHED);

    useEffect(() => {
        const fetchUserRoles = async (): Promise<void> => {
            try {
                await dispatch(getUserRoles());
            } finally {
                setShowLoader(false);
            }
        };

        void fetchUserRoles();
    }, []);

    return (
        <PageWrapper title={t('ORGANIZATION_LIST_PAGE.TITLE')} sx={styles.contentBody}>
            <Box sx={styles.contentBox}>
                <Box>
                    <Typography variant="h6">{t('ORGANIZATION_LIST_PAGE.LIST_OF_ORGANIZATIONS')}</Typography>
                    <Typography color={Colors.gray[500]}>
                        {t('ORGANIZATION_LIST_PAGE.LIST_OF_ORGANIZATIONS_DESCRIPTION')}
                    </Typography>
                </Box>
                <NavBarButton
                    startIcon={<AddCircle />}
                    buttonId={'create-organization-button'}
                    buttonText={t('ORGANIZATION_LIST_PAGE.CREATE_NEW_ORG')}
                    onClick={(): void => {
                        navigate('/organizations/create');
                    }}
                />
            </Box>

            {showLoader ? (
                <Spinner />
            ) : fetchedState === FetchedState.FAILED ? (
                <ErrorState />
            ) : organizations.length > 0 ? (
                <Box sx={styles.listBox}>
                    <ListOrganizations organizations={organizations} />
                </Box>
            ) : (
                <EmptyState
                    sx={styles.emptyState}
                    title={t('ORGANIZATION_LIST_PAGE.NO_ORGANIZATIONS_TITLE')}
                    description={t('ORGANIZATION_LIST_PAGE.NO_ORGANIZATIONS_DESCRIPTION')}
                    icon={<Group sx={styles.emptyStateIcon} />}
                />
            )}
        </PageWrapper>
    );
};
